import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import useApi from "../../../CustomHooks/useApi";

/* ── TABLE STYLES ── */
const TH = "border border-[#94A3B8] bg-[#EFF6FF] px-3 py-2.5 text-left text-[11px] font-medium text-[#0A2A4F] uppercase tracking-wider whitespace-nowrap select-none";
const TD = "border border-[#94A3B8] px-3 py-2.5 text-sm text-gray-700 align-middle whitespace-nowrap";

const today = () => new Date().toISOString().split("T")[0];

/* ══════════════════════════════════════════════ */
const CdmHistory = () => {

    const { callApi: getCdmHistory } = useApi("/admin/axis/cdm/history", "GET");

    const [data, setData]       = useState([]);
    const [loading, setLoading] = useState(false);
    const [toast, setToast]     = useState(null);

    /* ── filters ── */
    const [mid, setMid]           = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate]     = useState(today());

    /* ── toast ── */
    const showToast = (type, message) => {
        setToast({ type, message });
        setTimeout(() => setToast(null), 3500);
    };

    /* ── build query string ── */
    const buildQuery = (midVal, from, to) => {
        const params = [];
        if (midVal.trim()) params.push(`mid=${midVal.trim()}`);
        if (from)          params.push(`fromDate=${from}`);
        if (to)            params.push(`toDate=${to}`);
        return params.length ? `?${params.join("&")}` : null;
    };

    /* ── fetch ── */
    const fetchHistory = async (midVal = mid, from = fromDate, to = toDate) => {
        setLoading(true);
        try {
            const query = buildQuery(midVal, from, to);
            const res = await getCdmHistory(null, query);
            const rows = res?.content ?? (Array.isArray(res) ? res : []);
            setData(rows);
            if (rows.length === 0) showToast("error", "No records found for the selected filters.");
        } catch (err) {
            setData([]);
            showToast("error", err?.message || "Failed to fetch CDM history");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchHistory("", "", ""); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleReset = () => {
        setMid("");
        setFromDate("");
        setToDate(today());
        fetchHistory("", "", "");
    };

    /* ── excel download ── */
    const downloadExcel = () => {
        if (!data.length) return;
        const rows = data.map((row, idx) => ({
            "#":                idx + 1,
            "UTR":              row.utr ?? "-",
            "CDM Card No":      row.cdmCardNo ?? "-",
            "Agent Emboss Code":row.agentEmbossCode ?? "-",
            "BP User ID":       row.bpUserId ?? "-",
            "Shop Name":        row.shopName ?? "-",
            "Full Name":        row.fullName ?? "-",
            "Txn Amount":       row.txnAmount ?? "-",
            "Req Date Time":    row.reqDtTime ?? "-",
            "Status":           row.txnStatus ?? "-",
            "Response Message": row.responseMessage ?? "-",
        }));
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "CDM History");
        const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        saveAs(
            new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
            `cdm_history${mid ? `_${mid}` : ""}.xlsx`
        );
    };

    /* ══════════════ RENDER ══════════════ */
    return (
        <div className="w-full mx-auto pb-24">

            {/* TOAST */}
            {toast && (
                <div className={`fixed top-5 right-5 z-[60] flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg border text-sm font-medium transition-all
                    ${toast.type === "success" ? "bg-green-50 border-green-200 text-green-800" : "bg-red-50 border-red-200 text-red-800"}`}>
                    <span>{toast.type === "success" ? "✓" : "✕"}</span>
                    <span>{toast.message}</span>
                    <button onClick={() => setToast(null)} className="ml-2 opacity-60 hover:opacity-100 text-lg leading-none">✕</button>
                </div>
            )}

            {/* HEADER */}
            <div className="mb-6 bg-gradient-to-r from-[#0A2A4F] to-[#1a4a8f] rounded-2xl p-6 text-white shadow-lg">
                <h2 className="text-2xl font-bold">CDM Card History</h2>
                <p className="text-blue-200 text-sm mt-1">View CDM transaction history — filter by merchant or date range</p>
            </div>

            {/* FILTER CARD */}
            <div className="bg-white rounded-2xl shadow border px-5 py-4 mb-4">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Filters</p>
                <div className="flex flex-wrap gap-3 items-end">

                    {/* MID */}
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-gray-500">Merchant ID (MID)</label>
                        <input
                            type="text"
                            value={mid}
                            onChange={(e) => setMid(e.target.value)}
                            placeholder="e.g. M592297"
                            className="h-[40px] px-3 border border-[#E5E7EB] rounded-xl bg-white text-sm text-gray-700 focus:ring-2 focus:ring-[#0A2A4F]/20 focus:border-[#0A2A4F]/40 outline-none w-48"
                        />
                    </div>

                    {/* From Date */}
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-gray-500">From Date</label>
                        <input
                            type="date"
                            value={fromDate}
                            max={toDate || today()}
                            onChange={(e) => setFromDate(e.target.value)}
                            className="h-[40px] px-3 border border-[#E5E7EB] rounded-xl bg-white text-sm text-gray-700 focus:ring-2 focus:ring-[#0A2A4F]/20 focus:border-[#0A2A4F]/40 outline-none"
                        />
                    </div>

                    {/* To Date */}
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-gray-500">To Date</label>
                        <input
                            type="date"
                            value={toDate}
                            max={today()}
                            onChange={(e) => setToDate(e.target.value)}
                            className="h-[40px] px-3 border border-[#E5E7EB] rounded-xl bg-white text-sm text-gray-700 focus:ring-2 focus:ring-[#0A2A4F]/20 focus:border-[#0A2A4F]/40 outline-none"
                        />
                    </div>

                    {/* Apply */}
                    <button
                        onClick={() => fetchHistory()}
                        disabled={loading}
                        className="h-[40px] px-6 rounded-xl bg-[#0A2A4F] hover:bg-[#0d3566] disabled:opacity-50 text-white text-sm font-semibold transition-colors flex items-center gap-2"
                    >
                        {loading ? (
                            <>
                                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin inline-block" />
                                Fetching...
                            </>
                        ) : "Apply"}
                    </button>

                    {/* Reset */}
                    <button
                        onClick={handleReset}
                        disabled={loading}
                        className="h-[40px] px-5 rounded-xl border border-[#E5E7EB] bg-white hover:bg-gray-50 disabled:opacity-50 text-gray-600 text-sm font-semibold transition-colors"
                    >
                        Reset
                    </button>
                </div>
            </div>

            {/* TABLE CARD */}
            <div className="bg-white rounded-2xl shadow border overflow-hidden">

                    {/* Card header */}
                    <div className="px-5 py-4 border-b flex items-center justify-between gap-3">
                        <div>
                            <h3 className="text-base font-bold text-gray-800">CDM History</h3>
                            <p className="text-xs text-gray-400 mt-0.5">{data.length.toLocaleString("en-IN")} record{data.length !== 1 ? "s" : ""}</p>
                        </div>
                        <button
                            onClick={downloadExcel}
                            disabled={data.length === 0}
                            className="flex items-center gap-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors"
                        >
                            {`↓ Download Excel (${data.length})`}
                        </button>
                    </div>

                    {/* TABLE */}
                    <div className="overflow-x-auto">
                        {loading ? (
                            <div className="flex flex-col items-center gap-3 py-20">
                                <div className="w-10 h-10 border-4 border-[#0A2A4F] border-t-transparent rounded-full animate-spin"></div>
                                <p className="text-gray-400 text-sm">Fetching CDM history...</p>
                            </div>
                        ) : (
                            <table className="border-collapse text-sm" style={{ minWidth: 1100, width: "100%" }}>
                                <thead>
                                    <tr>
                                        <th className={TH}>#</th>
                                        <th className={TH}>UTR</th>
                                        <th className={TH}>CDM Card No</th>
                                        <th className={TH}>Agent Emboss Code</th>
                                        <th className={TH}>BP User ID</th>
                                        <th className={TH}>Shop Name</th>
                                        <th className={TH}>Full Name</th>
                                        <th className={`${TH} text-right`}>Txn Amount</th>
                                        <th className={TH}>Req Date Time</th>
                                        <th className={TH}>Status</th>
                                        <th className={TH}>Response Message</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.length === 0 ? (
                                        <tr>
                                            <td colSpan={11} className="border border-[#94A3B8] py-20 text-center">
                                                <div className="flex flex-col items-center gap-3">
                                                    <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center text-3xl shadow-inner">📭</div>
                                                    <p className="text-gray-500 text-sm font-medium">No CDM history records found</p>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        data.map((row, idx) => {
                                            const status = String(row.txnStatus ?? "").toUpperCase();
                                            const statusCls =
                                                status === "SUCCESS"
                                                    ? "bg-green-100 text-green-700 border-green-200"
                                                    : status === "FAILED" || status === "FAILURE"
                                                        ? "bg-red-100 text-red-700 border-red-200"
                                                        : status === "PENDING"
                                                            ? "bg-amber-100 text-amber-700 border-amber-200"
                                                            : "bg-gray-100 text-gray-600 border-gray-200";
                                            return (
                                                <tr key={row.utr ?? idx} className={idx % 2 === 0 ? "bg-white" : "bg-[#F8FAFC]"}>
                                                    <td className={TD}>{idx + 1}</td>
                                                    <td className={`${TD} font-mono text-xs text-gray-800`}>{row.utr ?? "-"}</td>
                                                    <td className={`${TD} font-mono font-semibold text-gray-900`}>{row.cdmCardNo ?? "-"}</td>
                                                    <td className={TD}>{row.agentEmbossCode ?? "-"}</td>
                                                    <td className={`${TD} font-medium text-[#0A2A4F]`}>{row.bpUserId ?? "-"}</td>
                                                    <td className={TD}>{row.shopName ?? "-"}</td>
                                                    <td className={TD}>{row.fullName ?? "-"}</td>
                                                    <td className={`${TD} text-right font-semibold text-gray-800`}>
                                                        {row.txnAmount !== undefined && row.txnAmount !== null
                                                            ? `₹${Number(row.txnAmount).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                                            : "-"}
                                                    </td>
                                                    <td className={TD}>{row.reqDtTime ?? "-"}</td>
                                                    <td className={TD}>
                                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-lg text-xs font-semibold border ${statusCls}`}>
                                                            {row.txnStatus ?? "-"}
                                                        </span>
                                                    </td>
                                                    <td className={TD}>{row.responseMessage ?? "-"}</td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>

                </div>

        </div>
    );
};

export default CdmHistory;
